import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { KPI_FILTER_CONFIGS } from '~/shared/constants/kpiFilters';
import {
  DEFAULT_REFETCH_INTERVAL_MS,
  UTILIZATION_TRAILING_LAG_MS,
} from '~/shared/constants/polling';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { GraphResponse, VolumeCountResponse, VolumesCapacityResponse } from '~/shared/types/api';
import { formatGiB, formatVolumeCount } from '~/shared/utils/formatNumber';
import {
  appendOrgAccountsScope,
  getVolumeDataQueryScopeParts,
} from '~/shared/utils/organizationUtils';
import { useXFetch } from '~/shared/utils/useXFetch';

const VOLUMES_BASE = '/api/v1/volumes';

export interface FleetManagerKpiValues {
  totalDetectedSize: string;
  totalDetectedCount: string;
  monitoredStorageSize: string;
  monitoredStorageCount: string;
  managedSize: string;
  managedVolumes: string;
  fullPotentialSize: string;
  potentiallySupportedVolumes: string;
  progressPercentage: number;
  currentUtilizationValue: string;
}

export interface FleetManagerKpiLoading {
  isTotalCardLoading: boolean;
  isMonitoredCardLoading: boolean;
  isPotentialCardLoading: boolean;
  isUtilizationCardLoading: boolean;
}

type AccountScope = ReturnType<typeof useAuthContext>['accounts'];
type TenantId = ReturnType<typeof useAuthContext>['activeTenantId'];

function buildScopedQuery(
  accounts: AccountScope,
  activeTenantId: TenantId,
  filter?: string
): string {
  const params = new URLSearchParams();
  if (filter) {
    params.set('filters', filter);
  }
  appendOrgAccountsScope(params, accounts, activeTenantId);
  return params.toString();
}

function buildUtilizationParams(accounts: AccountScope, activeTenantId: TenantId): string {
  const nowMs = Date.now();
  const end = new Date(nowMs - UTILIZATION_TRAILING_LAG_MS);
  const start = new Date(end.getTime() - 60 * 1000);
  const params = new URLSearchParams({
    start_date: start.toISOString(),
    end_date: end.toISOString(),
  });
  appendOrgAccountsScope(params, accounts, activeTenantId);
  return params.toString();
}

function isCardLoading(isReady: boolean, ...pending: boolean[]): boolean {
  return !isReady && pending.some(Boolean);
}

const refetchWhenDataExists = (query: { state: { data: unknown } }) =>
  query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false;

export function useFleetManagerKpiData(): {
  values: FleetManagerKpiValues;
  loading: FleetManagerKpiLoading;
} {
  const api = useXFetch();
  const { activeTenantId, accounts } = useAuthContext();

  const utilizationParams = useMemo(
    () => buildUtilizationParams(accounts, activeTenantId),
    [accounts, activeTenantId]
  );

  const apiFilters = useMemo(
    () => ({
      monitored: buildScopedQuery(accounts, activeTenantId, KPI_FILTER_CONFIGS.MONITORED),
      autoscalingOn: buildScopedQuery(accounts, activeTenantId, KPI_FILTER_CONFIGS.AUTOSCALING_ON),
      supportedOrPotential: buildScopedQuery(
        accounts,
        activeTenantId,
        KPI_FILTER_CONFIGS.SUPPORTED_OR_POTENTIAL
      ),
    }),
    [accounts, activeTenantId]
  );

  const orgScopeQuery = useMemo(
    () => buildScopedQuery(accounts, activeTenantId),
    [accounts, activeTenantId]
  );

  const volumeScopeKey = useMemo(
    () => getVolumeDataQueryScopeParts(accounts, activeTenantId),
    [accounts, activeTenantId]
  );

  const {
    data: utilizationData,
    isLoading: isLoadingUtilization,
    isFetching: isFetchingUtilization,
    isFetchedAfterMount: isFetchedAfterMountUtilization,
  } = useQuery<GraphResponse>({
      queryKey: ['currentUtilizationAvg', ...volumeScopeKey, utilizationParams],
      queryFn: () => api<GraphResponse>(`${VOLUMES_BASE}/utilization-graph?${utilizationParams}`),
      refetchInterval: refetchWhenDataExists,
    });

  const {
    data: capacityData,
    isLoading: isLoadingCapacity,
    isFetching: isFetchingCapacity,
    isFetchedAfterMount: isFetchedAfterMountCapacity,
  } = useQuery<VolumesCapacityResponse>({
      queryKey: ['volumesCapacity', ...volumeScopeKey],
      queryFn: () =>
        api<VolumesCapacityResponse>(
          orgScopeQuery ? `${VOLUMES_BASE}/capacity?${orgScopeQuery}` : `${VOLUMES_BASE}/capacity`
        ),
      refetchInterval: refetchWhenDataExists,
    });

  const {
    data: totalVolumeData,
    isLoading: isLoadingTotalCount,
    isFetching: isFetchingTotalCount,
    isFetchedAfterMount: isFetchedAfterMountTotalCount,
  } = useQuery<VolumeCountResponse>({
      queryKey: ['totalVolumeCount', ...volumeScopeKey],
      queryFn: () =>
        api<VolumeCountResponse>(
          orgScopeQuery ? `${VOLUMES_BASE}/count?${orgScopeQuery}` : `${VOLUMES_BASE}/count`
        ),
      refetchInterval: refetchWhenDataExists,
    });

  const {
    data: monitoredVolumeData,
    isLoading: isLoadingMonitoredCount,
    isFetching: isFetchingMonitoredCount,
    isFetchedAfterMount: isFetchedAfterMountMonitoredCount,
  } = useQuery<VolumeCountResponse>({
    queryKey: ['monitoredVolumeCount', KPI_FILTER_CONFIGS.MONITORED, ...volumeScopeKey],
    queryFn: () => api<VolumeCountResponse>(`${VOLUMES_BASE}/count?${apiFilters.monitored}`),
    refetchInterval: refetchWhenDataExists,
  });

  const {
    data: autoscalingVolumeData,
    isLoading: isLoadingAutoscalingCount,
    isFetching: isFetchingAutoscalingCount,
    isFetchedAfterMount: isFetchedAfterMountAutoscalingCount,
  } = useQuery<VolumeCountResponse>({
    queryKey: ['autoscalingVolumeCount', KPI_FILTER_CONFIGS.AUTOSCALING_ON, ...volumeScopeKey],
    queryFn: () => api<VolumeCountResponse>(`${VOLUMES_BASE}/count?${apiFilters.autoscalingOn}`),
    refetchInterval: refetchWhenDataExists,
  });

  const {
    data: monitoredCapacityData,
    isLoading: isLoadingMonitoredCapacity,
    isFetching: isFetchingMonitoredCapacity,
    isFetchedAfterMount: isFetchedAfterMountMonitoredCapacity,
  } = useQuery<VolumesCapacityResponse>({
    queryKey: ['monitoredCapacity', KPI_FILTER_CONFIGS.MONITORED, ...volumeScopeKey],
    queryFn: () => api<VolumesCapacityResponse>(`${VOLUMES_BASE}/capacity?${apiFilters.monitored}`),
    refetchInterval: refetchWhenDataExists,
  });

  const {
    data: autoscalingCapacityData,
    isLoading: isLoadingAutoscalingCapacity,
    isFetching: isFetchingAutoscalingCapacity,
    isFetchedAfterMount: isFetchedAfterMountAutoscalingCapacity,
  } = useQuery<VolumesCapacityResponse>({
    queryKey: ['autoscalingCapacity', KPI_FILTER_CONFIGS.AUTOSCALING_ON, ...volumeScopeKey],
    queryFn: () =>
      api<VolumesCapacityResponse>(`${VOLUMES_BASE}/capacity?${apiFilters.autoscalingOn}`),
    refetchInterval: refetchWhenDataExists,
  });

  const {
    data: potentiallySupportedVolumeData,
    isLoading: isLoadingPotentiallySupportedCount,
    isFetching: isFetchingPotentiallySupportedCount,
    isFetchedAfterMount: isFetchedAfterMountPotentiallySupportedCount,
  } = useQuery<VolumeCountResponse>({
    queryKey: [
      'potentiallySupportedVolumeCount',
      KPI_FILTER_CONFIGS.SUPPORTED_OR_POTENTIAL,
      ...volumeScopeKey,
    ],
    queryFn: () =>
      api<VolumeCountResponse>(`${VOLUMES_BASE}/count?${apiFilters.supportedOrPotential}`),
    refetchInterval: refetchWhenDataExists,
  });

  const {
    data: potentiallySupportedCapacityData,
    isLoading: isLoadingPotentiallySupportedCapacity,
    isFetching: isFetchingPotentiallySupportedCapacity,
    isFetchedAfterMount: isFetchedAfterMountPotentiallySupportedCapacity,
  } = useQuery<VolumesCapacityResponse>({
    queryKey: [
      'potentiallySupportedCapacity',
      KPI_FILTER_CONFIGS.SUPPORTED_OR_POTENTIAL,
      ...volumeScopeKey,
    ],
    queryFn: () =>
      api<VolumesCapacityResponse>(`${VOLUMES_BASE}/capacity?${apiFilters.supportedOrPotential}`),
    refetchInterval: refetchWhenDataExists,
  });

  const values = useMemo((): FleetManagerKpiValues => {
    const hasTotalCapacity = typeof capacityData?.capacity === 'number';
    const hasTotalCount = typeof totalVolumeData?.count === 'number';
    const hasMonitoredCapacity = typeof monitoredCapacityData?.capacity === 'number';
    const hasMonitoredCount = typeof monitoredVolumeData?.count === 'number';
    const hasManagedCount = typeof autoscalingVolumeData?.count === 'number';
    const hasManagedSize = typeof autoscalingCapacityData?.capacity === 'number';
    const hasFullPotentialCount = typeof potentiallySupportedVolumeData?.count === 'number';
    const hasFullPotentialSize = typeof potentiallySupportedCapacityData?.capacity === 'number';

    const managedSizeGiB = hasManagedSize ? autoscalingCapacityData.capacity : undefined;
    const fullPotentialSizeGiB = hasFullPotentialSize
      ? potentiallySupportedCapacityData.capacity
      : undefined;

    const progressPercentage =
      managedSizeGiB !== undefined &&
      fullPotentialSizeGiB !== undefined &&
      fullPotentialSizeGiB > 0
        ? Math.round((managedSizeGiB / fullPotentialSizeGiB) * 100)
        : 0;

    return {
      totalDetectedSize: hasTotalCapacity ? `${formatGiB(capacityData.capacity)}` : 'N/A',
      totalDetectedCount: hasTotalCount ? formatVolumeCount(totalVolumeData.count) : 'N/A Volumes',
      monitoredStorageSize: hasMonitoredCapacity
        ? `${formatGiB(monitoredCapacityData.capacity)}`
        : 'N/A',
      monitoredStorageCount: hasMonitoredCount
        ? formatVolumeCount(monitoredVolumeData.count)
        : 'N/A Volumes',
      managedSize: hasManagedSize ? formatGiB(autoscalingCapacityData.capacity) : 'N/A',
      managedVolumes: hasManagedCount
        ? formatVolumeCount(autoscalingVolumeData.count)
        : 'N/A Volumes',
      fullPotentialSize: hasFullPotentialSize
        ? formatGiB(potentiallySupportedCapacityData.capacity)
        : 'N/A',
      potentiallySupportedVolumes: hasFullPotentialCount
        ? formatVolumeCount(potentiallySupportedVolumeData.count)
        : 'N/A Volumes',
      progressPercentage,
      currentUtilizationValue:
        typeof utilizationData?.avgUtilization === 'number'
          ? `${utilizationData.avgUtilization.toFixed(0)}%`
          : 'N/A',
    };
  }, [
    capacityData,
    totalVolumeData,
    monitoredCapacityData,
    monitoredVolumeData,
    autoscalingVolumeData,
    autoscalingCapacityData,
    potentiallySupportedVolumeData,
    potentiallySupportedCapacityData,
    utilizationData,
  ]);

  const isTotalReady =
    typeof capacityData?.capacity === 'number' && typeof totalVolumeData?.count === 'number';
  const isMonitoredReady =
    typeof monitoredCapacityData?.capacity === 'number' &&
    typeof monitoredVolumeData?.count === 'number';
  const isPotentialReady =
    typeof autoscalingVolumeData?.count === 'number' &&
    typeof autoscalingCapacityData?.capacity === 'number' &&
    typeof potentiallySupportedVolumeData?.count === 'number' &&
    typeof potentiallySupportedCapacityData?.capacity === 'number';
  const isUtilizationReady = typeof utilizationData?.avgUtilization === 'number';

  const loading: FleetManagerKpiLoading = {
    isTotalCardLoading: isCardLoading(
      isTotalReady,
      isLoadingCapacity,
      isFetchingCapacity,
      isLoadingTotalCount,
      isFetchingTotalCount,
      !isFetchedAfterMountCapacity,
      !isFetchedAfterMountTotalCount
    ),
    isMonitoredCardLoading: isCardLoading(
      isMonitoredReady,
      isLoadingMonitoredCapacity,
      isFetchingMonitoredCapacity,
      isLoadingMonitoredCount,
      isFetchingMonitoredCount,
      !isFetchedAfterMountMonitoredCapacity,
      !isFetchedAfterMountMonitoredCount
    ),
    isPotentialCardLoading: isCardLoading(
      isPotentialReady,
      isLoadingAutoscalingCapacity,
      isFetchingAutoscalingCapacity,
      isLoadingAutoscalingCount,
      isFetchingAutoscalingCount,
      isLoadingPotentiallySupportedCapacity,
      isFetchingPotentiallySupportedCapacity,
      isLoadingPotentiallySupportedCount,
      isFetchingPotentiallySupportedCount,
      !isFetchedAfterMountAutoscalingCapacity,
      !isFetchedAfterMountAutoscalingCount,
      !isFetchedAfterMountPotentiallySupportedCapacity,
      !isFetchedAfterMountPotentiallySupportedCount
    ),
    isUtilizationCardLoading: isCardLoading(
      isUtilizationReady,
      isLoadingUtilization,
      isFetchingUtilization,
      !isFetchedAfterMountUtilization
    ),
  };

  return { values, loading };
}
